import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCampaign } from "@/contexts/CampaignContext";

interface Subscriber {
  id: string;
  email: string;
  status: string;
  start_date: string | null;
  created_at: string;
  openRate?: number;
  clickRate?: number;
  currentDay?: number | null;
}

export default function Subscribers() {
  console.log('Subscribers component rendered');
  const { selectedCampaignId } = useCampaign();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState<Subscriber | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get filters from URL
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "all";

  const formatSubscriberDate = (value: string | null) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return value;
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const { data: subscribers = [], isLoading } = useQuery({
    queryKey: ['subscribers', selectedCampaignId, search, statusFilter],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return [];

      // Build query with server-side filtering
      let query = supabase
        .from("subscribers")
        .select("*")
        .eq("campaign_id", selectedCampaignId)
        .order("created_at", { ascending: false });

      // Apply status filter if not "all"
      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      // Apply email search filter
      if (search) {
        query = query.ilike("email", `%${search}%`);
      }

      const { data, error } = await query;

      if (error || !data) return [];

      // Load engagement metrics for each subscriber
      const subscribersWithMetrics = await Promise.all(
        data.map(async (subscriber) => {
          // Get email logs for this subscriber
          const { data: emailLogs } = await supabase
            .from("email_logs")
            .select("day_number")
            .eq("subscriber_id", subscriber.id)
            .eq("status", "sent")
            .order("day_number", { ascending: false });

          // Get engagement events for this subscriber
          const { data: events } = await supabase
            .from("engagement_events")
            .select("event_type")
            .eq("subscriber_id", subscriber.id);

          const totalEmailsSent = emailLogs?.length || 0;
          const opens = events?.filter(e => e.event_type === "open").length || 0;
          const clicks = events?.filter(e => e.event_type === "click").length || 0;
          const currentDay = emailLogs && emailLogs.length > 0 ? emailLogs[0].day_number : null;

          return {
            ...subscriber,
            openRate: totalEmailsSent > 0 ? Math.round((opens / totalEmailsSent) * 100) : 0,
            clickRate: totalEmailsSent > 0 ? Math.round((clicks / totalEmailsSent) * 100) : 0,
            currentDay,
          };
        })
      );

      return subscribersWithMetrics;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (subscriberId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-subscriber', {
        body: {
          subscriberId,
          performedBy: 'admin'
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Subscriber deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setDeleteDialogOpen(false);
      setSubscriberToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete subscriber",
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (subscriber: Subscriber) => {
    setSubscriberToDelete(subscriber);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (subscriberToDelete) {
      deleteMutation.mutate(subscriberToDelete.id);
    }
  };

  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("search", value);
    } else {
      newParams.delete("search");
    }
    setSearchParams(newParams);
  };

  const handleStatusChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete("status");
    } else {
      newParams.set("status", value);
    }
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-success";
      case "pending":
        return "bg-warning";
      case "unsubscribed":
        return "bg-destructive";
      default:
        return "bg-muted";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-4">Subscribers</h1>

        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">Search by email</Label>
            <Input
              id="search"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="w-full sm:w-48">
            <Label htmlFor="status" className="sr-only">Filter by status</Label>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(search || statusFilter !== "all") && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          {isLoading ? "Loading..." : `Showing ${subscribers.length} subscriber${subscribers.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subscribers.length === 0 ? (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="py-8 text-center text-muted-foreground">
                No subscribers found for the selected filters.
              </CardContent>
            </Card>
          ) : (
            subscribers.map((subscriber) => (
          <Card key={subscriber.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="text-base">{subscriber.email}</span>
                <Badge className={getStatusColor(subscriber.status)}>
                  {subscriber.status}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                <div>
                  <span className="text-muted-foreground">Current Day:</span>{" "}
                  <span className="font-medium">
                    {subscriber.currentDay === null || subscriber.currentDay === 0
                      ? "Not Started"
                      : `Day ${subscriber.currentDay} (Sent)`}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Start Date:</span>{" "}
                  <span className="font-medium">
                    {formatSubscriberDate(subscriber.start_date) || "Not set"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Signed up:</span>{" "}
                  <span className="font-medium">
                    {new Date(subscriber.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Open Rate:</span>{" "}
                  <span className="font-medium text-blue-600">{subscriber.openRate}%</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Click Rate:</span>{" "}
                  <span className="font-medium text-green-600">{subscriber.clickRate}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <Link
                  to={`/admin/logs?search=${encodeURIComponent(subscriber.email)}`}
                  className="text-xs text-primary hover:underline"
                >
                  View Email History →
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(subscriber)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the subscriber{" "}
              <strong>{subscriberToDelete?.email}</strong> and all their associated data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSubscriberToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
